import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BedIcon, HomeIcon, UsersIcon, LucideIcon } from 'lucide-react';
import RoomCountSelect from './repat/RoomCountSelect';
import RoomsSection from './repat/RoomsSection';
import { ensureNumber } from './repat/utils';

interface RepatFormProps {
  readOnly?: boolean;
}

const RepatForm = ({ readOnly = false }: RepatFormProps) => {
  const { watch, setValue } = useFormContext();

  // Obtener valores de las secciones
  const totalRooms = ensureNumber(watch('repat.totalRooms', 0));
  const totalApartments = ensureNumber(watch('repat.totalApartments', 0));
  const totalCabins = ensureNumber(watch('repat.totalCabins', 0));
  
  const totalRoomPlaces = ensureNumber(watch('repat.totalRoomPlaces', 0));
  const totalApartmentPlaces = ensureNumber(watch('repat.totalApartmentPlaces', 0));
  const totalCabinPlaces = ensureNumber(watch('repat.totalCabinPlaces', 0));

  // Calcular totales generales
  const totalUnits = totalRooms + totalApartments + totalCabins;
  const totalPlaces = totalRoomPlaces + totalApartmentPlaces + totalCabinPlaces;

  // Actualizar totales generales en el formulario
  React.useEffect(() => {
    setValue('repat.totalUnits', totalUnits);
    setValue('repat.totalPlaces', totalPlaces);
  }, [totalUnits, totalPlaces, setValue]);

  return (
    <div className="space-y-8">
      {/* Sección de Totales Generales */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Totales Generales</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HomeIcon size={18} className="text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">Total Habitaciones</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-blue-600">{totalUnits}</span>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    Unidades
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover:bg-gray-50 transition-colors border border-gray-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UsersIcon size={18} className="text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">Total Plazas</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-blue-600">{totalPlaces}</span>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    Plazas
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sección de Habitaciones */}
      <RoomsSection readOnly={readOnly} />

      {/* Sección de Cabañas */}
      <RoomsSection readOnly={readOnly} isCabin />

      {/* Sección de Camping */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="camping-toggle"
            checked={watch('repat.camping', false)}
            onCheckedChange={(checked) => {
              setValue('repat.camping', checked);
              if (!checked) {
                setValue('repat.campingCapacity', 0);
              }
            }}
            disabled={readOnly}
          />
          <Label htmlFor="camping-toggle" className="font-medium">Camping</Label>
        </div>

        {watch('repat.camping', false) && (
          <div className="form-group ml-6 mt-2">
            <Label className="form-label">Capacidad</Label>
            <RoomCountSelect 
              icon={BedIcon}
              label="Capacidad"
              fieldName="repat.campingCapacity"
              defaultValue="0"
              readOnly={readOnly}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RepatForm;
